import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  InMemoryCache,
  split,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";

import {
  __GLOBAL_ASSESSMENT_ID,
  __GLOBAL_AUTH_TOKEN,
  __GLOBAL_SESSION_ID,
  __GLOBAL_TENANT,
  __GLOBAL_WORK_ID,
} from "@/client/globals";
import { API_ENDPOINT, WS_ENDPOINT } from "@/config";
import generatedIntrospection from "@/graphql/possible-types";
import { connectStudentChannel } from "@/stores/student-channel";

import { globalHeaders } from "./headers";

// Link to set some Authorization token headers and other useful debugging
// headers on the requests sent to Pantheon, from the currently available global
// information.
const headersLink = setContext(() => {
  const headers = globalHeaders();
  headers["x-cadmus-url"] = window.location.href;

  return {
    token: __GLOBAL_AUTH_TOKEN.current,
    headers,
  };
});

// Records and sets the Phoenix token by intercepting a GraphQL response which has a token.
// This also connects the global Phoenix "connection" with the parsed token.
const authLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((result) => {
    if (
      operation.operationName === "Work" &&
      operation.variables &&
      operation.variables.workId &&
      result.data &&
      result.data.token &&
      __GLOBAL_TENANT.current
    ) {
      // WorkQuery returns a token, which can be stored globally
      __GLOBAL_AUTH_TOKEN.current = result.data.token;
      // Connect the singleton StudentChannel using the Work ID.
      connectStudentChannel({
        channelType: "work",
        tenant: __GLOBAL_TENANT.current,
        workId: operation.variables.workId,
        userToken: result.data.token,
      });
    }

    // Handle Charter query token - set global token for subscriptions
    if (
      operation.operationName === "Charter" &&
      result.data &&
      result.data.token
    ) {
      // CharterQuery returns a token, which can be stored globally for subscriptions
      __GLOBAL_AUTH_TOKEN.current = result.data.token;
    }

    // Assign the global session ID from the response if available.
    if (
      result.data &&
      result.data.session &&
      result.data.session.__typename === "Session"
    ) {
      __GLOBAL_SESSION_ID.current = result.data.session.id ?? null;
    }

    return result;
  });
});

// Log any GraphQL errors or network error that occurred
const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
  if (networkError && networkError.name === "AbortError") {
    // Suppress AbortError
    return;
  }
  if (graphQLErrors)
    graphQLErrors.map(({ message, locations, path }) =>
      console.error(
        `[GraphQL error]: Message: ${message}, Operation: ${operation.operationName} Location: ${locations}, Path: ${path}`
      )
    );
  if (networkError) console.error(`[Network error]: ${networkError}`);
});

// Apollo Link for Retry Logic on WorkDocument query
const workQueryRetryLink = new RetryLink({
  delay: {
    initial: Math.random() * 1500,
    max: 3000,
    jitter: true,
  },
  attempts: {
    max: 10,
    retryIf: (_error, operation) => {
      return operation.operationName === "Work";
    },
  },
});

// Main WS Link to the Pantheon GraphQL subscriptions websocket.
const wsLink = new GraphQLWsLink(
  createClient({
    url: `${WS_ENDPOINT}/graphql`,
    shouldRetry: () => true,
    connectionParams: () => {
      return {
        role: "student",
        token: __GLOBAL_AUTH_TOKEN.current,
        tenant: __GLOBAL_TENANT.current,
      };
    },
  })
);

// Main HTTP link to the Pantheon GraphQL API
const httpLink = new HttpLink({
  uri: `${API_ENDPOINT}/api/graphql`,
  credentials: "include",
});

// Terminating Apollo link which splits the Queries and Mutations (Pantheon HTTP
// API) from the Graphql Subscriptions (over Websocket)
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  httpLink
);

/** Singleton Apollo graphql normalised cache. */
export const cache = new InMemoryCache({
  possibleTypes: generatedIntrospection.possibleTypes,
  typePolicies: {
    Institution: {
      // There is only a Singleton Institution
      keyFields: [],
    },
    Assessment: {
      // There is only a Singleton Assessment
      keyFields: [],
    },
    Session: {
      // There is only a Singleton Session
      keyFields: [],
    },
    InstitutionFeatures: {
      // There is only a Singleton InstitutionFeatures per institution
      keyFields: [],
    },
    Group: {
      fields: {
        members: {
          merge(_existing, incoming) {
            return incoming;
          },
        },
      },
    },
    BlockSnapshot: {
      /**
       * We don't store multiple BlockSnapshots for the same AnswerBlock x Type
       * in the cache. This overrides the existing cached BlockSnapshot (per
       * AnswerBlock) with the new one.
       */
      keyFields: ["cacheId"],
    },
  },
});

/** Singleton apollo client for the aphrodite application. */
export const client = new ApolloClient({
  cache: cache,
  link: from([headersLink, authLink, workQueryRetryLink, errorLink, splitLink]),
});
